import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dentallyLogo from "../../../assets/dentally-logo.png";
import type { TermsSection } from "./types";
import {
  BackButton,
  BrandImage,
  ContentCard,
  HeaderBlock,
  PageTitle,
  PageWrapper,
  SectionBlock,
  SectionHeading,
  SectionParagraph,
  SubSectionHeading,
  Subtitle,
  TopBar,
  UpdatedText,
} from "./style";

const TERMS_SECTIONS: TermsSection[] = [
  {
    heading: "1. Definitions and Acceptance",
    blocks: [
      {
        subheading: "Definitions",
        paragraphs: [
          "Services: Refers collectively to the AI-powered receptionist platform, including inbound/outbound call handling, appointment management, message notification services, and associated digital tools provided by Smart Forum.",
          "PHI (Protected Health Information): Any information relating to an individual's past, present, or future physical or mental health condition, the provision of healthcare to that individual, or payment for the provision of such care, as defined under HIPAA.",
          "Personal Data: Any information relating to an identified or identifiable natural person (as defined by GDPR).",
          "Client/Practice: Refers to the dental, medical, or healthcare practice entity subscribing to and utilizing the Services.",
        ],
      },
      {
        subheading: "Acceptance of Terms",
        paragraphs: [
          "By using the Platform, you represent that:",
          "a) You are legally authorized to enter into this agreement on behalf of the Client/Practice.",
          "b) You have read, understood, and agreed to comply with all provisions contained herein.",
          "c) If you are entering into these Terms for a healthcare entity, you acknowledge your understanding of HIPAA compliance requirements and the necessity of executing a Business Associate Agreement (BAA) with Smart Forum.",
        ],
      },
    ],
  },
  {
    heading: "2. Scope of Services and Critical Disclaimers",
    blocks: [
      {
        subheading: "Service Architecture and Third-Party Integration",
        paragraphs: [
          'The Services are provided through a sophisticated, multi-layered platform architecture that integrates with various specialized third-party services ("Third-Party Processors"). These processors include but are not limited to: Twilio (telephony), Vapi (voice API), OpenAI/LLM Providers, Self hosted N8N (workflow automation), and other cloud service providers.',
          "a) Role of Third Parties: Smart Forum acts solely as the integrator and interface provider. The core functions—such as call routing, message transcription, data storage, and workflow execution—are executed by these independent third-party processors.",
          "b) Data Flow Disclaimer: When a booking or appointment is made, user data (including patient names, contact numbers, and scheduling details) is transmitted from the Client to Smart Forum, then passed through one or more Third-Party Processors for processing, and finally written into databases managed by those respective third parties.",
          "c) No Ownership of Processor Data: Smart does not own, control, nor assume responsibility for the security, integrity, or compliance of data stored within the systems of any Third-Party Processor. The Client acknowledges that it must manage its contractual relationships with these processors to ensure continuous compliance.",
        ],
      },
      {
        subheading: "Critical Disclaimer: No Medical Advice or Triage",
        paragraphs: [
          "THIS SECTION IS OF THE UTMOST IMPORTANCE AND MUST BE READ CAREFULLY.",
          "Nature of Service: Apointlo (Smart Forum) is strictly a communication, scheduling, and administrative tool. It is not a medical provider, diagnostic service, or clinical decision-making platform.",
          "Prohibited Functions: The AI system does not, and is not designed to, perform any of the following: Medical or dental triage, diagnosis, or assessment of symptoms; Recommendation of treatments, medications, procedures, or dosages; Assessment of the urgency or severity of a patient's condition (e.g., determining if a situation is an emergency); Provision of any clinical, medical, or dental advice whatsoever.",
          "Clinical Responsibility: All clinical judgment, emergency protocols, treatment decisions, and ultimate responsibility for patient care remain the sole and exclusive duty of the licensed healthcare provider (the Client/Practice).",
          "Emergency Protocol: When a caller describes symptoms or potential emergencies, the AI system shall strictly follow the pre-defined, written emergency protocol provided by the Client/Practice. Smart Forum bears no liability for clinical outcomes resulting from the reliance on information provided through this platform.",
        ],
      },
    ],
  },
  {
    heading: "3. Client Responsibilities and Compliance",
    blocks: [
      {
        subheading: "Client Obligations",
        paragraphs: [
          "The Client warrants and agrees that it shall be solely responsible for:",
          "Providing accurate, complete, and current practice details, including service offerings, pricing, and insurance verification protocols.",
          "Defining, documenting, and providing clear emergency handling instructions and call scripts to the Client's designated point of contact for review and approval by Smart Forum.",
          "Ensuring that its use of the Services complies with all applicable federal, state, local laws, including but not limited to HIPAA, GDPR, NHS and telecommunications laws.",
          "Obtaining all necessary patient consents for call recording and data processing as required by law in the Client's jurisdiction (e.g., one-party vs. two-party consent states).",
        ],
      },
      {
        subheading: "Data Ownership and Use",
        paragraphs: [
          "a) Ownership: The Client retains full ownership of all practice content, patient data, PHI, and materials provided to Smart Forum.",
          "b) License Grant: By providing such content, the Client grants Smart Forum a limited, non-exclusive license solely for the purpose of delivering the contracted Services.",
          "c) Use Limitation: The Client shall not use the Services or any derived data for any commercial purpose other than following up on contacts first made through the platform.",
        ],
      },
    ],
  },
  {
    heading: "4. Data Handling, Security, and Privacy",
    blocks: [
      {
        subheading: "Data Processing Roles and Third-Party Processors",
        paragraphs: [
          "Smart Forum acknowledges its role as a Data Processor concerning PHI and Personal Data processed on behalf of the Client/Practice, which acts as the Data Controller. The processing involves multiple independent third parties:",
          "a) Third-Party Processor Management: Smart Forum shall exercise due diligence to ensure that all Third-Party Processors utilized (e.g., self-hosted N8N, VAPI, Twilio, OpenAI) are capable of meeting industry-standard security requirements and can execute necessary Business Associate Agreements (BAAs) or Data Processing Agreements (DPAs).",
          "b) Client Responsibility for Vetting: The Client acknowledges that the ultimate responsibility for vetting the compliance status (HIPAA/GDPR) of any specific Third-Party Processor utilized in each workflow rests with the Client. Smart Forum provides integration, but not the guarantee of the underlying processor's compliance.",
          "c) Data Flow Accountability: The Client must ensure that all data transmitted to and stored by third parties is necessary for the contracted service and complies with applicable law.",
        ],
      },
      {
        subheading: "Security Breach Protocol",
        paragraphs: [
          "In the event of a Security Breach, Smart Forum shall notify the Client promptly (within 48 hours). However, if the breach originates from a Third-Party Processor's system failure or security lapse, Smart Forum's obligation to investigate and mitigate is limited solely to its ability to communicate with that specific processor.",
        ],
      },
    ],
  },
  {
    heading: "5. Service Plans, Billing, and Termination",
    blocks: [
      {
        subheading: "Receptionist Service Plans",
        paragraphs: [
          'Smart Forum offers "Receptionist Service Plans" on a month-to-month subscription basis. Details are available at apointlo.com. Changes will take effect on the next monthly payment date. Overage charges, if any, will apply until the new plan takes effect.',
          'Definition of Calls: "Receptionist Calls" means: 1) incoming calls made by third parties or forwarded by you to your assigned number (Subscriber Identifier), and that you identify as desired calls; and 2) outbound calls made by Smart Forum at your request. Inbound calls from telemarketers, misdialed numbers, or promotional companies are not counted as Receptionist Calls.',
          "Compliance: If utilizing outbound calling, the Client must comply with all applicable laws, including without limitation the Telephone Consumer Protection Act (47 U.S.C. § 227) and the Telemarketing Sales Rule (16 C.F.R. 310 et seq.). Outbound calling services must not be used for spam or fraud.",
          "Billing Cycle: The monthly rate is charged on the Subscription Date and will automatically charge each month on the anniversary date until termination.",
        ],
      },
      {
        subheading: "AI Receptionist Services",
        paragraphs: [
          "Smart Forum provides AI (artificial intelligence) receptionist services, including AI-led answering, filtering spam calls, etc. All plans are subject to Smart Forum's Reasonable Use Policy. Overage charges for exceeding the number of AI Calls will be charged on your next monthly charge or within 10 days of termination.",
        ],
      },
      {
        subheading: "Outreach Services",
        paragraphs: [
          "Smart Forum offers outreach services (one-time model or recurring subscription). The Client must comply with all applicable laws, including but not limited to the Telephone Consumer Protection Act and the Telemarketing Sales Rule. Overage charges will be charged on the last business day of each month or within 10 days of termination.",
        ],
      },
      {
        subheading: "Subscription Billing Cycle",
        paragraphs: [
          "All payments are non-refundable. The monthly rate is charged on the Subscription Date and will automatically charge each month on the anniversary date until you terminate your subscription. If you sign up on April 10, you will be automatically charged the monthly rate for your chosen Service Plan on the 10th day of each month thereafter until your subscription is canceled.",
        ],
      },
      {
        subheading: "Termination",
        paragraphs: [
          "Smart Forum reserves the right to terminate services immediately if timely payment is not made. If payment is not made within five (5) business days, we will stop providing all Services; if payment is not made within ten (10) business days, Smart Forum will terminate your account.",
        ],
      },
    ],
  },
  {
    heading: "6. General Provisions and Limitation of Liability",
    blocks: [
      {
        subheading: "Indemnification",
        paragraphs: [
          "The Client agrees to indemnify, defend, and hold harmless Smart Forum, its directors, officers, employees, agents, successors, assigns, and affiliates from and against all third-party claims, judgments, costs, liabilities, damages or expenses, including reasonable attorneys' fees and costs, arising out of, resulting in any way from, or made by:",
          "i) A breach of any provision, representation, or warranty set forth in these Terms;",
          "ii) Callers using the Client's Subscriber Identifier as a result of actions of or failure to act by the Client; and",
          "iii) The failure, malfunction, data loss, security breach, or non-compliance of any Third-Party Processor utilized in connection with the Services.",
        ],
      },
      {
        subheading: "Limitation of Liability",
        paragraphs: [
          'A. Exclusion of Third-Party Liability: Smart Forum shall not be liable for any damages, losses, or failures arising from the performance, security, data handling, or operational failure of any integrated Third-Party Processor (including but not limited to Twilio, Vapi, OpenAI, Self hosted N8N). The Client acknowledges that these third parties are independent contractors, and their services are provided "as is."',
          "B. Limitation Cap: To the maximum extent permitted by law, in no event will Smart Forum be liable to the Client or any other party for any indirect, special, incidental, consequential, punitive, or exemplary damages, regardless of the basis or nature of the claim or legal theory, whether in tort, contract or otherwise, resulting from the Services, any use of the Application, or anything pertaining to these Terms, including without limitation any loss of goodwill, lost profits, business interruption, loss of data, or cost of procurement of substitute services, even if such party has expressly been advised of the possibility of such damages. In no event will the aggregate liability for any and all of Client's claims against Smart Forum under or in connection with this Agreement or arising out of or related to Services or Application exceed the amounts actually paid by Client to Smart Forum during the 12 months prior to the date a claim is made.",
        ],
      },
      {
        subheading: "Disclaimer of Warranties",
        paragraphs: [
          "THE APPLICATION AND SERVICES ARE PROVIDED “AS IS” WITHOUT WARRANTY OF ANY KIND EXCEPT AS EXPLICITLY SET FORTH IN THIS AGREEMENT, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT, OR ACCURACY OF CONTENT. SMART FORUM MAKES NO WARRANTY OR REPRESENTATION THAT CLIENT'S USE OF THE APPLICATION OR SERVICES WILL MEET CLIENT'S REQUIREMENTS OR WILL BE UNINTERRUPTED OR ERROR-FREE. WITHOUT LIMITING THE FOREGOING, NEITHER SMART FORUM NOR ANYONE ASSOCIATED WITH SMART FORUM REPRESENTS OR WARRANTS THAT THE PLATFORM, ITS CONTENT, LEADS, OR THE SERVICES WILL BE COMPLETE, ACCURATE, RELIABLE, ERROR-FREE, OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT THE PLATFORM OR THE SERVER THAT MAKES IT OR THE SERVICES AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT THE PLATFORM OR THE SERVICES WILL OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS. THE FOREGOING DOES NOT AFFECT ANY WARRANTIES WHICH CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.",
        ],
      },
      {
        subheading: "Governing Law and Dispute Resolution",
        paragraphs: [
          "These Terms shall be governed by and construed in accordance with the internal laws of the State of Pakistan, without giving effect to any choice or conflict of law provisions or rules. Any disputes arising out of or related to these Terms shall be resolved exclusively in the state or federal courts located in Pakistan, Islamabad.",
        ],
      },
    ],
  },
  {
    heading: "7. Contact Information",
    blocks: [
      {
        paragraphs: [
          "For all questions, support requests, or legal notices regarding this agreement, please contact:",
          "Smart Forum Legal Department",
          "Email: apointlo@smartforum.org",
          "Support Portal: https://apointlo.com/",
        ],
      },
    ],
  },
];

export default function TermsAndConditionsPage() {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <ContentCard vertical>
        <TopBar align="center" justify="space-between">
          <BrandImage src={dentallyLogo} alt="Dentally" preview={false} />
        </TopBar>

        <BackButton
          aria-label="Go back"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        />

        <HeaderBlock vertical>
          <PageTitle level={1}>Terms of Service Agreement</PageTitle>
          <Subtitle>Smart Forum AI Receptionist Platform (“Apointlo”)</Subtitle>
          <UpdatedText>
            Last Updated: 9th June 2026 | Effective Date: 5th June 2026
          </UpdatedText>
        </HeaderBlock>

        {TERMS_SECTIONS.map((section) => (
          <SectionBlock key={section.heading} vertical>
            <SectionHeading level={2}>{section.heading}</SectionHeading>
            {section.blocks.map((block, blockIndex) => (
              <SectionBlock key={blockIndex} vertical>
                {block.subheading && (
                  <SubSectionHeading level={3}>
                    {block.subheading}
                  </SubSectionHeading>
                )}
                {block.paragraphs.map((paragraph, index) => (
                  <SectionParagraph key={index}>{paragraph}</SectionParagraph>
                ))}
              </SectionBlock>
            ))}
          </SectionBlock>
        ))}
      </ContentCard>
    </PageWrapper>
  );
}
